/**
 * Uncaught browser errors → POST /api/client-errors → the same production
 * error log as server failures (src/util/log.ts, CloudWatch in production).
 *
 * Best-effort by design: at most MAX_REPORTS per page load, one report per
 * distinct message (a render loop can throw the same error thousands of
 * times), a failed POST is dropped rather than retried, and nothing here
 * throws. Signed-out pages get a 401 and report nothing — the endpoint sits
 * behind the session gate.
 */

const MAX_REPORTS = 20;
const MAX_STACK = 4000;

/** The page path with a share link's token masked — the log must never hold a working share link. */
export function reportablePage(pathname: string): string {
  return pathname.replace(/^\/share\/[^/]+/, '/share/:token');
}

/** The proposal id from a `/proposals/:id...` (or `/print/:id`) path, when the current page is scoped to one — docs/observability.md §4's optional `proposalId`. */
export function proposalIdFromPath(pathname: string): string | undefined {
  const match = /^\/(?:proposals|print)\/([^/]+)/.exec(pathname);
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

/**
 * The chat run currently in flight, when one is — set by the chat view as a
 * turn starts/ends (best-effort; there is no wiring for this yet, so it
 * stays undefined until a caller sets it, same as `proposalId` staying
 * undefined off a proposal page). Module-level, not per-report, since a
 * report can fire from anywhere a turn is running.
 */
let currentRunId: string | undefined;
export function setCurrentRunId(runId: string | undefined): void {
  currentRunId = runId;
}

export interface ClientErrorReport {
  kind: 'error' | 'unhandledrejection';
  message: string;
  stack?: string;
  /** docs/observability.md §4 calls this `url`; kept as `page` too for the existing log field. */
  page: string;
  proposalId?: string;
  runId?: string;
}

interface ErrorReportingHost {
  addEventListener: Window['addEventListener'];
  location: { pathname: string };
}

export function installErrorReporting(
  host: ErrorReportingHost = window,
  send: (report: ClientErrorReport) => Promise<unknown> = postReport,
): void {
  const seen = new Set<string>();
  const report = (kind: ClientErrorReport['kind'], reason: unknown): void => {
    try {
      const err = reason instanceof Error ? reason : null;
      const message = err ? `${err.name}: ${err.message}` : String(reason);
      if (seen.has(message) || seen.size >= MAX_REPORTS) return;
      seen.add(message);
      const stack = err?.stack?.slice(0, MAX_STACK);
      const page = reportablePage(host.location.pathname);
      const proposalId = proposalIdFromPath(host.location.pathname);
      send({
        kind,
        message,
        ...(stack ? { stack } : {}),
        page,
        ...(proposalId ? { proposalId } : {}),
        ...(currentRunId ? { runId: currentRunId } : {}),
      }).catch(() => undefined);
    } catch {
      // Reporting an error must never raise another one.
    }
  };
  // Bubble phase on window: script errors only — a failed <img>/<script>
  // load does not bubble, so broken images never reach the log.
  host.addEventListener('error', (event) => report('error', (event as ErrorEvent).error ?? (event as ErrorEvent).message));
  host.addEventListener('unhandledrejection', (event) =>
    report('unhandledrejection', (event as PromiseRejectionEvent).reason),
  );
}

function postReport(report: ClientErrorReport): Promise<unknown> {
  // docs/observability.md §4's field name is `url`; the server route accepts
  // both `url` and the older `page` name (src/server/index.ts /client-errors).
  const { page, ...rest } = report;
  return fetch('/api/client-errors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    // Survives a navigation or tab close that follows the error.
    keepalive: true,
    body: JSON.stringify({ ...rest, page, url: page }),
  });
}
